import React, {memo} from 'react';
import {Theme} from '@mui/material/styles';
import {WithStyles} from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import MuiDialog, {DialogProps as MuiDialogProps} from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import Typography from '@mui/material/Typography';
import {green} from '@mui/material/colors';
import classnames from 'classnames';
import {Backdrop, CircularProgress} from '@mui/material';
import {DialogContextProvider} from 'common/context/DialogContext';

const styles = (theme: Theme) =>
   createStyles({
      root: {
         margin: 0,
         padding: theme.spacing(2),
         color: 'white',
         backgroundColor: green[500],
      },
      rigthButtonSection: {
         position: 'absolute',
         top: theme.spacing(1),
         right: theme.spacing(1),
      },
      closeButton: {
         color: 'white',
      },
      launchButton: {
         color: 'white',
      },
   });

export interface DialogTitleProps extends WithStyles<typeof styles> {
   id: string;
   children: React.ReactNode;
   className?: string;
   onClose?: () => void;
   onLaunch?: () => void;
}
export const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
   const {children, classes, onClose, onLaunch, className, ...other} = props;
   return (
      <MuiDialogTitle className={classnames(classes.root, className)} {...other}>
         <Typography variant="h6" component="span">
            {children}
         </Typography>
         <div className={classes.rigthButtonSection}>
            {onLaunch && (
               <IconButton
                  aria-label="launch"
                  className={classes.launchButton}
                  onClick={onLaunch}
                  size="large"
               >
                  <LaunchIcon />
               </IconButton>
            )}
            {onClose ? (
               <IconButton
                  aria-label="close"
                  className={classes.closeButton}
                  onClick={onClose}
                  size="large"
               >
                  <CloseIcon />
               </IconButton>
            ) : null}
         </div>
      </MuiDialogTitle>
   );
});

export const DialogContent = withStyles((theme: Theme) => ({
   root: {
      position: 'relative',
      padding: theme.spacing(2),
      whiteSpace: 'pre-wrap',
      display: 'flex',
      flexFlow: 'column',
   },
}))(MuiDialogContent);

export type DialogProps = {
   close?: () => void;
   open?: boolean;
   children?: any;
   header?: React.ReactNode;
   loading?: boolean;

   maxWidth?: MuiDialogProps['maxWidth'];
   fullWidth?: MuiDialogProps['fullWidth'];
   classes?: MuiDialogProps['classes'];
   sx?: MuiDialogProps['sx'];
};
const Dialog = memo((props: DialogProps) => {
   const {
      close,
      open = true,
      header,
      maxWidth: width,
      children,
      classes,
      fullWidth = true,
      sx,
   } = props;
   const onClose = React.useCallback(
      (event?: {}, reason?: string) => reason !== 'backdropClick' && close?.(),
      [close]
   );
   const [loading, setLoading] = React.useState(false);
   return (
      <MuiDialog
         onClose={onClose}
         maxWidth={width}
         fullWidth={fullWidth}
         aria-labelledby="customized-dialog-title"
         open={open}
         classes={classes}
         sx={sx}
      >
         <DialogContextProvider value={{loading, setLoading}}>
            <DialogTitle id="customized-dialog-title" onClose={onClose}>
               {header}
            </DialogTitle>
            <DialogContent dividers>
               <Backdrop
                  sx={{
                     position: 'absolute',
                     color: '#fff',
                     zIndex: (theme) => theme.zIndex.drawer + 1,
                  }}
                  open={loading || Boolean(props.loading)}
                  //   onClick={handleClose}
               >
                  <CircularProgress color="inherit" />
               </Backdrop>
               {children}
            </DialogContent>
         </DialogContextProvider>
         {/* <DialogActions>
            <Button autoFocus onClick={handleClose} color="primary">
               Save changes
            </Button>
         </DialogActions> */}
      </MuiDialog>
   );
});

export default Dialog;
